
import {
    Component,
    Emit,
    Prop,
    PropSync,
    Vue,
    Watch,
} from 'vue-property-decorator';
import { LoginStatus, WechatApi } from '@/request/wechat';
import { IUser } from '@/common/entity/User';
import { Message } from 'element-ui';
import { Response } from '@/common/entity/Response';
import { logError, logToConsole } from '@/common/LogHelper';
import { WxLoginQrCode } from '@/common/entity/WxLoginQrCode';
import { appEnv } from '@/common/AppConst';

@Component
export default class WxQrCodeLoginPanel extends Vue {
    /**
     * 扫码状态返回异常后重试的最大次数
     */
    readonly maxStatusAgainCount = 5;

    @Prop({ type: Boolean, default: true })
    readonly showErrorToast!: boolean;

    @Prop({ type: Boolean, default: false })
    readonly visiable!: boolean;

    /**
     * 二维码
     */
    qrCode: WxLoginQrCode | null = null;

    /**
     * 覆盖在二维码上方的提示语言
     */
    qrCodeTip: string = '';

    /**
     * 是否正在轮循获取扫描登录状态
     */
    gettingStatus: boolean = false;

    /**
     * 是否刷新登录二维码
     */
    refreshQrCode: boolean = false;

    /**
     * 二维码图片地址
     */
    get qrCodeUrl(): string | undefined {
        return this.qrCode?.qrUrl;
    }

    @Watch('visiable', { immediate: true })
    onVisiableChange(newVal: boolean, oldVal: boolean) {
        if (newVal === oldVal) return;
        if (!newVal) return;
        if (this.gettingStatus) this.refreshQrCode = true;
        else this.getCodeOrDirectLogin();
    }

    mounted() {
        logToConsole('wx-qrcode-login-panel mounted');
    }

    getCodeOrDirectLogin() {
        //个人电脑本地登录测试使用
        if (appEnv.isLocalServer) {
            this.qrCode = {
                ticket: 'directLogin',
                qrUrl: 'directLogin',
                expireTime: Number.MAX_VALUE,
            };
            this.reqLoginStatus();
            return;
        }

        this.getLoginQrCode();
    }

    /**
     * 点击刷新二维码按钮
     */
    clickRefreshBtn() {
        this.getCodeOrDirectLogin();
    }

    async getLoginQrCode() {
        try {
            this.refreshQrCode = false;
            const qrCode = await WechatApi.loginQrCode(false);
            if (!qrCode)
                throw Response.newForException(
                    Response.MSG_SERVER_EXCEPTION,
                    '获取登录二维码接口返回的二维码信息为空'
                );
            this.qrCode = qrCode;
            this.qrCodeTip = '';
            if (this.gettingStatus) return;
            setTimeout(() => {
                this.reqLoginStatus();
            }, 1000);
        } catch (error) {
            logError(error);
            this.catchError(error);
        }
    }

    async reqLoginStatus(errorCount: number = 0) {
        if (!this.visiable || !this.qrCode?.ticket) {
            this.gettingStatus = false;
            return;
        }

        if (this.refreshQrCode) {
            this.gettingStatus = false;
            this.getLoginQrCode();
            return;
        }

        this.gettingStatus = true;

        try {
            const loginStatus = await WechatApi.qrCodeLoginStatus(
                this.qrCode?.ticket,
                this.showErrorToast
            );

            if (loginStatus?.hasException)
                throw Response.newForException(
                    Response.MSG_SERVER_EXCEPTION,
                    '登录二维码状态响应hasException字段为true'
                );

            switch (loginStatus?.status) {
                case LoginStatus.STATUS_SUCCESS: //扫描成功
                    if (!loginStatus?.user) {
                        throw Response.newForException(
                            Response.MSG_SERVER_EXCEPTION,
                            '二维码状态为扫描成功时用户信息为空'
                        );
                    }
                    this.success(loginStatus.user);
                    break;
                case LoginStatus.STATUS_SCANED: //用户已经扫描，但还未有扫描结果
                    this.qrCodeTip = '扫描成功<br />正在登录...'; //提示用户已经扫描
                case LoginStatus.STATUS_WAIT_SCAN: //用户未扫描
                    //一秒后继续调用
                    setTimeout(() => {
                        this.reqLoginStatus();
                    }, 1000);
                    break;
                case LoginStatus.STATUS_REFRESH:
                    //需要刷新二维码，一般是二维码已过期的情况
                    this.qrCodeTip = '二维码已过期<br />请点击下方“刷新二维码”';
                    this.gettingStatus = false;
                    break;
                default:
                    throw Response.newForException(
                        Response.MSG_SERVER_EXCEPTION,
                        '服务器返回的二维码状态异常'
                    );
            }
        } catch (error) {
            logError(error);
            if (errorCount > this.maxStatusAgainCount) this.catchError(error);
            else
                setTimeout(() => {
                    this.reqLoginStatus(++errorCount);
                }, 1000);
        }
    }

    catchError(error: any) {
        if (error instanceof Response) this.error(error.userMsg);
        else this.error(Response.MSG_APP_EXCEPTION);
    }

    @Emit()
    error(errMsg: string) {
        this.gettingStatus = false;
        if (this.showErrorToast) Message.error(errMsg);
    }

    @Emit()
    success(user: IUser) {
        this.gettingStatus = false;
    }
}
